import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Download, FileUp, Lock, Upload } from "lucide-react";
import { useAttendanceCodeConfig } from "../../../../context/attendanceCodeConfigContext";
import Modal from "../../masters/Modal";
import { usePermissions } from "../../../../rbac/PermissionsContext";
import {
  DsAlert,
  DsBadge,
  DsButton,
  DsIcon,
  DsInput,
  DsLabel,
  DsPageHeader,
  DsSpinner,
} from "../../../../design-system";

const DEFAULT_CONFIGS = [
  { meaning: "Present", originalCode: "PR", customCode: "PR", importCode: "PR", exportCode: "PR" },
  { meaning: "Absent", originalCode: "LOP", customCode: "LP", importCode: "LP", exportCode: "LP" },
  { meaning: "Weekly Off", originalCode: "WO", customCode: "WO", importCode: "WO", exportCode: "WO" },
  { meaning: "Public Holiday", originalCode: "PH", customCode: "PH", importCode: "PH", exportCode: "PH" },
];

function mapConfigRow(c) {
  return {
    meaning: c.meaning || "",
    originalCode: (c.originalCode || c.original || c.original_code || "").toUpperCase(),
    customCode: (
      c.customCode || c.custom || c.custom_code || c.originalCode || ""
    ).toUpperCase(),
    importCode: (c.importCode || c.import_code || c.originalCode || "").toUpperCase(),
    exportCode: (c.exportCode || c.export_code || c.originalCode || "").toUpperCase(),
  };
}

function parseCodesCsv(text) {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length < 2) return [];
  const header = lines[0].toLowerCase();
  const hasHeader =
    header.includes("meaning") ||
    header.includes("default") ||
    header.includes("import");
  const dataLines = hasHeader ? lines.slice(1) : lines;
  const rows = [];
  for (const line of dataLines) {
    const cols = line.split(",").map((c) => c.replace(/^"|"$/g, "").trim());
    if (cols.length < 4) continue;
    const [meaning, originalCode, importCode, exportCode] = cols;
    const orig = (originalCode || importCode || "").toUpperCase();
    if (!orig) continue;
    rows.push({
      meaning: meaning || orig,
      originalCode: orig,
      customCode: orig,
      importCode: (importCode || orig).toUpperCase(),
      exportCode: (exportCode || orig).toUpperCase(),
    });
  }
  return rows;
}

/**
 * Import / export attendance code mapping (default PR, LOP, WO, PH + custom rows).
 */
const AttendanceCodeMappingPanel = ({ darkMode }) => {
  const {
    loading,
    error,
    fetchConfigurations,
    saveConfigurations,
    resetConfigurations,
    halfDayDivider,
    halfDayPrefix,
  } = useAttendanceCodeConfig();
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [halfDayDividerLocal, setHalfDayDividerLocal] = useState(halfDayDivider ?? "");
  const [halfDayPrefixLocal, setHalfDayPrefixLocal] = useState(halfDayPrefix ?? "");
  const importInputRef = useRef(null);

  const { hasScreenAccess, isAdmin } = usePermissions();
  // Prefer Attendance → Settings → Code mapping; fall back to configuration.attendance_codes.
  const canEdit =
    isAdmin ||
    hasScreenAccess("attendance", "attendance_code_mapping", "edit") ||
    hasScreenAccess("configuration", "attendance_codes", "edit");
  const canExport =
    isAdmin ||
    hasScreenAccess("attendance", "attendance_code_mapping", "edit") ||
    hasScreenAccess("configuration", "attendance_codes", "export") ||
    hasScreenAccess("configuration", "attendance_codes", "edit");
  const canView =
    isAdmin ||
    hasScreenAccess("attendance", "attendance_code_mapping", "view") ||
    hasScreenAccess("configuration", "attendance_codes", "view");

  useEffect(() => {
    setHalfDayDividerLocal(
      halfDayDivider != null && halfDayDivider !== "" ? halfDayDivider : ""
    );
    setHalfDayPrefixLocal(
      halfDayPrefix != null && halfDayPrefix !== "" ? halfDayPrefix : ""
    );
  }, [halfDayDivider, halfDayPrefix]);

  useEffect(() => {
    const load = async () => {
      const configs = await fetchConfigurations();
      const codesArray = Array.isArray(configs)
        ? configs
        : configs?.codes || configs?.configurations || [];
      if (!codesArray?.length) {
        setRows(DEFAULT_CONFIGS.map(mapConfigRow));
      } else {
        setRows(codesArray.map(mapConfigRow));
      }
    };
    if (canView) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  const handleChange = (idx, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: String(value || "").toUpperCase() };
      return next;
    });
  };

  const handleSave = async () => {
    if (!canEdit) {
      toast.error("You do not have permission to save attendance codes.");
      return;
    }
    try {
      setSaving(true);
      const codes = rows.map((r) => ({
        meaning: r.meaning,
        customCode: (r.customCode || r.originalCode || "").toUpperCase(),
        importCode: (r.importCode || "").toUpperCase(),
        exportCode: (r.exportCode || "").toUpperCase(),
      }));
      for (const c of codes) {
        if (!c.importCode) {
          toast.error(`Import code for "${c.meaning}" cannot be empty`);
          setSaving(false);
          return;
        }
        if (!c.exportCode) {
          toast.error(`Export code for "${c.meaning}" cannot be empty`);
          setSaving(false);
          return;
        }
      }
      const resp = await saveConfigurations({
        codes,
        halfDayDivider:
          halfDayDividerLocal.trim() === "" ? null : halfDayDividerLocal.trim(),
        halfDayPrefix:
          halfDayPrefixLocal.trim() === "" ? null : halfDayPrefixLocal.trim(),
      });
      const saved = Array.isArray(resp)
        ? resp
        : resp?.data ?? resp?.codes ?? resp?.configurations ?? [];
      if (saved?.length) setRows(saved.map(mapConfigRow));
      else {
        const fresh = await fetchConfigurations();
        setRows((fresh || DEFAULT_CONFIGS).map(mapConfigRow));
      }
      toast.success("Code mapping saved");
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const confirmReset = async () => {
    setShowResetModal(false);
    if (!canEdit) return;
    try {
      setSaving(true);
      const resp = await resetConfigurations();
      const configs = Array.isArray(resp)
        ? resp
        : resp?.data ?? resp?.codes ?? resp?.configurations ?? [];
      setRows(configs.map(mapConfigRow));
      toast.success("Reset to default codes");
    } catch (err) {
      toast.error(err?.message || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  const exportCsv = () => {
    if (!canExport) {
      toast.error("You do not have permission to export.");
      return;
    }
    const headers = ["Meaning", "Default Code", "Import Code", "Export Code"];
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [
      headers.join(","),
      ...rows.map((r) =>
        [r.meaning, r.originalCode, r.importCode, r.exportCode].map(escape).join(",")
      ),
    ];
    const blob = new Blob([lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "attendance_code_mapping.csv";
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("Exported code mapping");
  };

  const downloadTemplate = () => {
    if (!canExport) return;
    const headers = ["Meaning", "Default Code", "Import Code", "Export Code"];
    const sample = ["Present", "PR", "PR", "PR"];
    const blob = new Blob([[headers.join(","), sample.join(",")].join("\r\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "attendance_codes_import_template.csv";
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("Template downloaded");
  };

  const onImportFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !canEdit) return;
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCodesCsv(reader.result);
      if (!parsed.length) {
        toast.error("No valid rows found in CSV");
        return;
      }
      setRows(parsed);
      toast.success(`Loaded ${parsed.length} code row(s). Click Save to apply.`);
    };
    reader.readAsText(file);
  };

  if (!canView) {
    return (
      <div className="ds-page-panel flex min-h-[12rem] flex-1 items-center justify-center p-6">
        <p className="ds-body text-[hsl(var(--ds-muted-fg))]">
          You do not have permission to view attendance code mapping.
        </p>
      </div>
    );
  }

  if (loading && rows.length === 0) {
    return (
      <div className="ds-page-panel flex min-h-[12rem] flex-1 items-center justify-center">
        <DsSpinner />
      </div>
    );
  }

  return (
    <div className="ds-page-panel flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="ds-page-panel-head">
        {error && (
          <div className="mb-3">
            <DsAlert tone="danger">{error?.message || String(error)}</DsAlert>
          </div>
        )}
        <DsPageHeader
          title="Import & export code mapping"
          description="Map default attendance codes (PR, LOP, WO, PH) to your import/export file formats. Used across attendance grid, calendar, and Excel import/export."
          actions={
            <>
              <input
                ref={importInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={onImportFile}
              />
              <DsButton
                type="button"
                variant="outline"
                size="sm"
                disabled={!canEdit}
                onClick={() => importInputRef.current?.click()}
              >
                <DsIcon icon={FileUp} size="sm" />
                Import CSV
              </DsButton>
              <DsButton
                type="button"
                variant="outline"
                size="sm"
                disabled={!canExport || rows.length === 0}
                onClick={exportCsv}
              >
                <DsIcon icon={Upload} size="sm" />
                Export CSV
              </DsButton>
              <DsButton
                type="button"
                variant="outline"
                size="sm"
                disabled={!canExport}
                onClick={downloadTemplate}
              >
                <DsIcon icon={Download} size="sm" />
                Template
              </DsButton>
            </>
          }
        />
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5 lg:p-6">
        <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted)/0.35)] p-4">
          <h5 className="ds-h4 mb-1 text-[hsl(var(--ds-fg))]">Half-day import &amp; export format</h5>
          <p className="ds-caption mb-3 text-[hsl(var(--ds-muted-fg))]">
            Used for both import and export. Leave blank to use plain codes (e.g.{" "}
            <strong>PRLOP</strong>). Example: prefix <strong>1/2</strong> and divider{" "}
            <strong>+</strong> → <strong>1/2EL+1/2ML</strong>
          </p>
          <div className="flex flex-wrap gap-4">
            <div>
              <DsLabel htmlFor="half-day-divider">Divider</DsLabel>
              <DsInput
                id="half-day-divider"
                type="text"
                value={halfDayDividerLocal}
                onChange={(e) => setHalfDayDividerLocal(e.target.value)}
                placeholder="+"
                maxLength={10}
                className="max-w-[8rem]"
                disabled={!canEdit}
              />
            </div>
            <div>
              <DsLabel htmlFor="half-day-prefix">Prefix (optional)</DsLabel>
              <DsInput
                id="half-day-prefix"
                type="text"
                value={halfDayPrefixLocal}
                onChange={(e) => setHalfDayPrefixLocal(e.target.value)}
                placeholder="1/2"
                maxLength={20}
                className="max-w-[8rem]"
                disabled={!canEdit}
              />
            </div>
          </div>
        </div>

        <div className="ds-table-wrap min-h-0 max-h-none flex-1 overflow-auto">
          <table className="ds-table">
            <thead>
              <tr>
                <th>Meaning</th>
                <th>Default code</th>
                <th>Import code</th>
                <th>Export code</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.originalCode}-${i}`}>
                  <td>{r.meaning}</td>
                  <td className="font-mono">{r.originalCode}</td>
                  <td>
                    <DsInput
                      value={r.importCode}
                      onChange={(e) => handleChange(i, "importCode", e.target.value)}
                      className="max-w-[8rem]"
                      disabled={!canEdit}
                    />
                  </td>
                  <td>
                    <DsInput
                      value={r.exportCode}
                      onChange={(e) => handleChange(i, "exportCode", e.target.value)}
                      className="max-w-[8rem]"
                      disabled={!canEdit}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center gap-3 pb-1">
          <DsButton
            type="button"
            onClick={handleSave}
            disabled={saving || !canEdit}
            loading={saving}
          >
            {saving ? "Saving…" : "Save mapping"}
          </DsButton>
          <DsButton
            type="button"
            variant="outline"
            onClick={() => setShowResetModal(true)}
            disabled={saving || !canEdit}
          >
            Reset defaults
          </DsButton>
          {!canEdit && (
            <DsBadge tone="warning" className="gap-1">
              <DsIcon icon={Lock} size="sm" />
              View only
            </DsBadge>
          )}
        </div>
      </div>

      <Modal
        darkMode={darkMode}
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        title="Reset to defaults"
        onSubmit={confirmReset}
        submitText="Reset"
        onCancel={() => setShowResetModal(false)}
      >
        <p className="ds-body text-[hsl(var(--ds-muted-fg))]">
          Reset all import/export codes and half-day format to system defaults?
        </p>
      </Modal>
    </div>
  );
};

export default AttendanceCodeMappingPanel;
